import { useState } from "react";
import type { SuccessStory } from "@/mocks/successStories";

interface SuccessStoryCardProps {
  story: SuccessStory;
}

export default function SuccessStoryCard({ story }: SuccessStoryCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article
      className="rounded-3xl border-[3px] border-gray-900 overflow-hidden"
      style={{ background: "#FFFFFF" }}
      dir="rtl"
    >
      {/* Top section: image + basic info */}
      <div className="p-4 pb-3">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="flex-shrink-0 w-16 h-16 rounded-2xl border-[2.5px] border-gray-900 overflow-hidden">
            <img
              src={story.image}
              alt={story.name}
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-black text-gray-900 leading-tight">
              {story.name}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <i className="ri-map-pin-line text-gray-400 text-xs"></i>
              <span className="text-xs font-medium text-gray-500">
                {story.location}
              </span>
              <span className="text-gray-300 text-xs">|</span>
              <span className="text-xs font-medium text-gray-500">
                {story.age} שנים
              </span>
            </div>

            {/* Before / After */}
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs font-semibold text-gray-400 bg-gray-100 rounded-lg px-2 py-0.5">
                {story.beforeRole}
              </span>
              <i className="ri-arrow-left-line text-gray-400 text-xs"></i>
              <span
                className="text-xs font-bold rounded-lg px-2 py-0.5"
                style={{
                  background: story.programColor,
                  color: "#1F2937",
                }}
              >
                {story.afterRole}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quote */}
      <div className="px-4 pb-3">
        <div
          className="rounded-2xl border-[2px] border-gray-200 p-3 relative"
          style={{ background: "#FFFBF5" }}
        >
          <i className="ri-double-quotes-l text-gray-300 text-lg absolute top-2 right-3"></i>
          <p className="text-sm font-semibold text-gray-700 leading-relaxed pr-6">
            {story.quote}
          </p>
        </div>
      </div>

      {/* Tags */}
      <div className="px-4 pb-3 flex flex-wrap gap-1.5">
        {story.tags.map((tag) => (
          <span
            key={tag}
            className="text-xs font-bold rounded-full px-3 py-1 border-[1.5px] border-gray-200"
            style={{ background: "#FAF9F6", color: "#4B5563" }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Expandable story */}
      <div className="px-4 pb-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between rounded-2xl border-[2px] border-gray-200 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
          style={{ background: "#FAF9F6" }}
        >
          <span className="text-sm font-bold text-gray-700">
            {expanded ? "סגור את הסיפור" : "קרא/י את הסיפור המלא"}
          </span>
          <i
            className={`ri-arrow-down-s-line text-gray-500 text-lg transition-transform ${expanded ? "rotate-180" : ""}`}
          ></i>
        </button>

        {expanded && (
          <div className="mt-3 rounded-2xl border-[2px] border-gray-200 p-4" style={{ background: "#FFFBF5" }}>
            <p className="text-sm font-medium text-gray-600 leading-relaxed">
              {story.storyText}
            </p>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
              <i className="ri-time-line text-gray-400 text-sm"></i>
              <span className="text-xs font-semibold text-gray-500">
                משך התהליך: {story.duration}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Program badge */}
      <div className="px-4 pb-4">
        <div
          className="flex items-center gap-2 rounded-2xl border-[2px] border-gray-900 px-4 py-2.5"
          style={{ background: story.programColor }}
        >
          <i className="ri-award-line text-gray-800 text-sm"></i>
          <span className="text-sm font-bold text-gray-800">
            {story.program}
          </span>
        </div>
      </div>
    </article>
  );
}
